import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Alert,
  Snackbar,
  IconButton,
  Chip,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import BackupIcon from "@mui/icons-material/Backup";
import RestoreIcon from "@mui/icons-material/Restore";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import WarningIcon from "@mui/icons-material/Warning";
import api from "../../services/api";

interface Backup {
  fileName: string;
  size: number;
  createdAt: string;
  path: string;
}

const DatabaseBackupManagement: React.FC = () => {
  const [backups, setBackups] = useState<Backup[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    action: "",
    fileName: "",
    message: "",
  });

  useEffect(() => {
    fetchBackups();
  }, []);

  const fetchBackups = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get("/admin/database/backups");
      setBackups(response.data.backups);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to fetch backups");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBackup = async () => {
    setCreating(true);
    setError(null);
    try {
      const response = await api.post("/admin/database/backup");
      setSuccess(
        `Backup created successfully: ${response.data.fileName} (${formatFileSize(response.data.size)})`,
      );
      fetchBackups();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to create backup");
    } finally {
      setCreating(false);
    }
  };

  const handleRestoreBackup = async (fileName: string) => {
    setRestoring(true);
    setError(null);
    try {
      await api.post("/admin/database/restore", { fileName });
      setSuccess(`Database restored successfully from: ${fileName}`);
      closeConfirmDialog();
      fetchBackups();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to restore backup");
    } finally {
      setRestoring(false);
    }
  };

  const handleDownloadBackup = async (fileName: string) => {
    try {
      const response = await api.get(`/admin/database/backups/${fileName}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setSuccess(`Backup downloaded: ${fileName}`);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to download backup");
    }
  };

  const handleDeleteBackup = async (fileName: string) => {
    setError(null);
    try {
      await api.delete(`/admin/database/backups/${fileName}`);
      setSuccess(`Backup deleted: ${fileName}`);
      closeConfirmDialog();
      fetchBackups();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to delete backup");
    }
  };

  const openConfirmDialog = (action: string, fileName: string) => {
    let message = "";
    if (action === "restore") {
      message = `Are you sure you want to restore the database from "${fileName}"? This will replace all current data with the data from this backup. This action cannot be undone.`;
    } else if (action === "delete") {
      message = `Are you sure you want to delete the backup "${fileName}"? This action cannot be undone.`;
    }

    setConfirmDialog({
      open: true,
      action,
      fileName,
      message,
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({
      open: false,
      action: "",
      fileName: "",
      message: "",
    });
  };

  const handleConfirmAction = () => {
    if (confirmDialog.action === "restore") {
      handleRestoreBackup(confirmDialog.fileName);
    } else if (confirmDialog.action === "delete") {
      handleDeleteBackup(confirmDialog.fileName);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <Box>
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h5">Database Backup & Restore</Typography>
        <Box>
          <Button
            startIcon={<RefreshIcon />}
            onClick={fetchBackups}
            variant="outlined"
            sx={{ mr: 1 }}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            startIcon={<BackupIcon />}
            onClick={handleCreateBackup}
            variant="contained"
            disabled={creating}
          >
            {creating ? "Creating..." : "Create Backup"}
          </Button>
        </Box>
      </Box>

      {/* Warning Alert */}
      <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
        <Typography variant="body2" fontWeight="bold">
          Important Notice:
        </Typography>
        <Typography variant="body2">
          • Restoring a backup will replace ALL current data in the database.
          <br />
          • Always create a backup before restoring to ensure you can recover if
          needed.
          <br />
          • Database operations may take several minutes depending on size.
          <br />• Only restore backups when the system is not in active use.
        </Typography>
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {creating && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" gutterBottom>
            Creating database backup...
          </Typography>
          <LinearProgress />
        </Box>
      )}

      {restoring && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" gutterBottom>
            Restoring database... Please wait.
          </Typography>
          <LinearProgress color="warning" />
        </Box>
      )}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Backup File Name</TableCell>
                <TableCell>Size</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : backups.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body2" color="textSecondary">
                      No backups found. Create your first backup to get started.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                backups.map((backup) => (
                  <TableRow key={backup.fileName} hover>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {backup.fileName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={formatFileSize(backup.size)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{formatDate(backup.createdAt)}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleDownloadBackup(backup.fileName)}
                        title="Download backup"
                      >
                        <DownloadIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="warning"
                        onClick={() =>
                          openConfirmDialog("restore", backup.fileName)
                        }
                        title="Restore from this backup"
                        disabled={restoring}
                      >
                        <RestoreIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() =>
                          openConfirmDialog("delete", backup.fileName)
                        }
                        title="Delete backup"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={closeConfirmDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {confirmDialog.action === "restore"
            ? "Confirm Restore"
            : "Confirm Delete"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmDialog.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmDialog} disabled={restoring}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            color={confirmDialog.action === "restore" ? "warning" : "error"}
            variant="contained"
            disabled={restoring}
          >
            {restoring
              ? "Restoring..."
              : confirmDialog.action === "restore"
                ? "Restore"
                : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSuccess(null)}
          severity="success"
          sx={{ width: "100%" }}
        >
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DatabaseBackupManagement;
